import { area, bbox as turfBbox, degreesToRadians, lengthToDegrees } from '@turf/turf';
import { intersection } from 'polyclip-ts';
import RBush from 'rbush';
import { ATTRS, GRID_TYPES, TAGS } from '../constants';
import { EntitiesManager } from '../entitiesManager';
import { Entity, ISOFileInformation } from '../types';

const GRID_CELL_SIZE = 10; // meters

const defaultGridParamsGenerator = (geometry: any) => {
  const [minX, minY, maxX, maxY] = turfBbox(geometry);

  const sizeY = lengthToDegrees(GRID_CELL_SIZE, 'meters');
  const sizeX = sizeY / Math.cos(degreesToRadians((minY + maxY) / 2)); // works fine for small areas

  const numCols = Math.floor((maxX - minX) / sizeX);
  const numRows = Math.floor((maxY - minY) / sizeY);

  return {
    minX,
    minY,
    numCols,
    numRows,
    cellWidth: (maxX - minX) / numCols,
    cellHeight: (maxY - minY) / numRows,
  };
};

export class GridEntity implements Entity {
  public static fromXML(xml, matchIDs: any, files: ISOFileInformation[]) {
    const xmlAttrs = xml._attributes;

    const filename = xmlAttrs[ATTRS.Grid.Filename];
    const regex = new RegExp(`${filename}\\.BIN$`, 'i');
    const file = files.find((f) => !!f.filename.match(regex));
    const data: Uint8Array = file.data as Uint8Array;

    const cells = new Int32Array(data.buffer);
    const rows = parseInt(xmlAttrs[ATTRS.Grid.GridMaximumRow], 10);
    const cols = parseInt(xmlAttrs[ATTRS.Grid.GridMaximumColumn], 10);
    const w = parseFloat(xmlAttrs[ATTRS.Grid.GridCellEastSize]);
    const h = parseFloat(xmlAttrs[ATTRS.Grid.GridCellNorthSize]);
    const minX = parseFloat(xmlAttrs[ATTRS.Grid.GridMinimumEastPosition]);
    const minY = parseFloat(xmlAttrs[ATTRS.Grid.GridMinimumNorthPosition]);

    const features = new Array(rows * cols);

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const dose = cells[y * cols + x];
        if (dose === 0.0) {
          features[y * cols + x] = null;
          continue;
        }
        features[y * cols + x] = {
          type: 'Feature',
          properties: {
            DOSE: dose,
          },
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                [minX + x * w, minY + y * h],
                [minX + x * w + w, minY + y * h],
                [minX + x * w + w, minY + y * h + h],
                [minX + x * w, minY + y * h + h],
                [minX + x * w, minY + y * h],
              ],
            ],
          },
        };
      }
    }
    const geometry = {
      type: 'FeatureCollection',
      features: features.filter((e) => e),
    };

    return {
      entity: new GridEntity(geometry, parseInt(xmlAttrs[ATTRS.Grid.TreatmentZoneCode], 10)),
    };
  }
  public type = TAGS.Grid;
  public geometry: any;

  constructor(
    geometry: any,
    private treatmentZoneCode: number
  ) {
    this.geometry = geometry;
  }

  public toXML(xmlId: string, entitiesManager: EntitiesManager) {
    const gridParamsGenerator =
      entitiesManager.options.gridRaramsGenerator || defaultGridParamsGenerator;
    const { minX, minY, numCols, numRows, cellWidth, cellHeight } = gridParamsGenerator(
      this.geometry
    );

    const filelength = numCols * numRows * 4;
    const buffer = new ArrayBuffer(filelength);
    const int32array = new Int32Array(buffer);

    const tree = new RBush();
    tree.load(
      this.geometry.features.map((f) => {
        const [bboxMinX, bboxMinY, bboxMaxX, bboxMaxY] = turfBbox(f);
        return { minX: bboxMinX, minY: bboxMinY, maxX: bboxMaxX, maxY: bboxMaxY, feature: f };
      })
    );

    for (let y = 0; y < numRows; y++) {
      for (let x = 0; x < numCols; x++) {
        // Simplified algorithm: just check the center of cell
        // const lng = minX + (x + 0.5) * cellWidth
        // const lat = minY + (y + 0.5) * cellHeight
        // const searchResults = tree.search({minX: lng, minY: lat, maxX: lng, maxY: lat})
        // const feature = searchResults.find(res => booleanPointInPolygon([lng, lat], res.feature))?.feature

        const searchResults = tree.search({
          minX: minX + x * cellWidth,
          minY: minY + y * cellHeight,
          maxX: minX + (x + 1) * cellWidth,
          maxY: minY + (y + 1) * cellHeight,
        });

        const cell = [
          [
            [minX + x * cellWidth, minY + y * cellHeight],
            [minX + x * cellWidth, minY + (y + 1) * cellHeight],
            [minX + (x + 1) * cellWidth, minY + (y + 1) * cellHeight],
            [minX + (x + 1) * cellWidth, minY + y * cellHeight],
            [minX + x * cellWidth, minY + y * cellHeight],
          ],
        ];

        let feature = null;
        let maxArea = 0;
        searchResults.forEach((res) => {
          const intersectionRes = intersection(res.feature.geometry.coordinates, cell);
          if (intersection.length) {
            const intersectionArea = area({ type: 'MultiPolygon', coordinates: intersectionRes });
            if (intersectionArea > maxArea) {
              feature = res.feature;
              maxArea = intersectionArea;
            }
          }
        });

        const value = feature ? feature.properties.DOSE : 0;

        int32array[y * numCols + x] = value;
      }
    }

    const filename = entitiesManager.addFile(TAGS.Grid, buffer);

    return {
      _attributes: {
        [ATTRS.Grid.GridMinimumNorthPosition]: minY,
        [ATTRS.Grid.GridMinimumEastPosition]: minX,
        [ATTRS.Grid.GridCellNorthSize]: cellHeight,
        [ATTRS.Grid.GridCellEastSize]: cellWidth,
        [ATTRS.Grid.GridMaximumColumn]: numCols,
        [ATTRS.Grid.GridMaximumRow]: numRows,
        [ATTRS.Grid.Filename]: filename,
        [ATTRS.Grid.Filelength]: filelength,
        [ATTRS.Grid.GridType]: GRID_TYPES.TYPE_2,
        [ATTRS.Grid.TreatmentZoneCode]: this.treatmentZoneCode,
      },
    };
  }
}
